import { LOCAL_STORAGE_KEY } from '../constants';
import { GeocodeAddress } from '../types';
import { sanitizeStorageValue } from './sanitizeStorageValue';

type LOCAL_STORAGE_KEYS = LOCAL_STORAGE_KEY | GeocodeAddress;

/**
 * Stores values in `localStorage`
 * @param key a key from the `LOCAL_STORAGE_KEY` enum
 * @param data the data to be stored
 * @param replacer a custom function to replace values before storing
 */
export const setLocalData = (
  key: LOCAL_STORAGE_KEYS,
  data: unknown,
  replacer?: (this: unknown, key: string, value: unknown) => unknown
): void => {
  localStorage.setItem(key, JSON.stringify(data, replacer));
};

/**
 * Retrieves values from the `localStorage`
 * @param key a key from the `LOCAL_STORAGE_KEY` enum
 * @param reviver a custom function to retrieve a stored value
 * @returns the stored value or `null`
 */
export const getLocalData = <T>(
  key: LOCAL_STORAGE_KEYS,
  reviver?: (this: unknown, key: string, value: unknown) => unknown
): T | undefined => {
  const data = localStorage.getItem(key);

  return data ? sanitizeStorageValue<T>(data, reviver) : undefined;
};

/**
 * Appends one value to the `localStorage` and updates the storage with it
 * @param key a key for the `LOCAL_STORAGE_KEY` enum
 * @param data the new value to store
 * @returns void
 */
export const appendLocalData = <T>(key: LOCAL_STORAGE_KEYS, data: T): void => {
  const list = getLocalData<T[]>(key) ?? [];

  setLocalData(key, [...list, data]);
};

/**
 * Pulls all "matching" values from the `localStorage`, these values are removed from the storage
 * @param key a key for the `LOCAL_STORAGE_KEY` enum
 * @param matchingFn the function that matches the desired values to pull
 * @returns the list of values matching the given function
 */
export const pullLocalData = <T>(
  key: LOCAL_STORAGE_KEY,
  matchingFn: (item: T) => boolean
): T[] => {
  const list = getLocalData<T[]>(key) ?? [];
  const [pulledItems, ignoredItems] = list.reduce<[T[], T[]]>(
    ([pulled, ignored], item) =>
      matchingFn(item)
        ? [[...pulled, item], ignored]
        : [pulled, [...ignored, item]],
    [[], []]
  );

  setLocalData(key, ignoredItems);

  return pulledItems;
};

/**
 * Removes a value from `localStorage` using a key
 * @param key a key from the `LOCAL_STORAGE_KEY` enum
 */
export const removeLocalData = (key: LOCAL_STORAGE_KEYS): void => {
  localStorage.removeItem(key);
};

/**
 * Stores values in `sessionStorage`
 * @param key a key from the `LOCAL_STORAGE_KEY` enum
 * @param data the data to be stored
 */
export const setSessionData = (
  key: LOCAL_STORAGE_KEYS,
  data: unknown
): void => {
  sessionStorage.setItem(key, JSON.stringify(data));
};

/**
 * Retrieves values from `sessionStorage`
 * @param key a key from the `LOCAL_STORAGE_KEY` enum
 * @returns the stored value or `null`
 */
export const getSessionData = <T>(key: LOCAL_STORAGE_KEYS): T | undefined => {
  const data = sessionStorage.getItem(key);

  return data ? JSON.parse(data) : undefined;
};
